// The quiz as a dialog. When the corner nudge shows lives in CopilotNudge;
// this is only the dialog: dimmed backdrop,
// escape and backdrop close, focus kept inside and restored on close, and a
// stated time cost in the header so the interruption is honest about its ask.
//
// Two ways in:
//  - a click (the /get-started CTAs): opens at once and shows a loading state
//    while quiz_bootstrap arrives, so the button never looks dead
//  - the browse pop-up (waitForData): renders nothing until the data is in,
//    then calls onShown, so a slow phone is never shown an empty box
import { useEffect, useRef } from "react";
import CopilotQuiz from "./CopilotQuiz";
import { useQuizBootstrap } from "./useQuizData";
import { siteEvents } from "@/lib/siteEvents";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

const MODAL_CSS = `
.afq-backdrop { position:fixed; inset:0; z-index:1000; background:rgba(15,33,55,.62); display:flex; align-items:center; justify-content:center; padding:20px; }
.afq-dialog { width:100%; max-width:560px; height:min(720px, calc(100vh - 40px)); height:min(720px, calc(100dvh - 40px)); background:#fff; border-radius:20px; box-shadow:0 2px 6px rgba(15,33,55,.08), 0 24px 56px -30px rgba(15,33,55,.5); overflow:hidden; display:flex; flex-direction:column; }
.afq-head { background:#0F2137; padding:18px 26px; display:grid; grid-template-columns:minmax(0,1fr) auto; column-gap:14px; align-items:center; flex-shrink:0; }
.afq-body { padding:24px 30px 26px; overflow-y:auto; flex:1; overscroll-behavior:contain; }
.afq-close:focus-visible { outline:2px solid #2BC97F; outline-offset:2px; }
@media (max-width: 520px) {
  .afq-backdrop { padding:10px; }
  .afq-dialog { height:calc(100vh - 20px); height:calc(100dvh - 20px); border-radius:16px; }
  .afq-head { padding:14px 16px; }
  .afq-body { padding:18px 18px 20px; }
}
`;

export default function QuizModal({
  open, onClose, source = "get-started", waitForData = false, onShown,
}: {
  open: boolean;
  onClose: () => void;
  source?: string;
  waitForData?: boolean;
  onShown?: () => void;
}) {
  const { data, error, retry } = useQuizBootstrap();
  const dialogRef = useRef<HTMLDivElement>(null);
  const visible = open && (!waitForData || !!data);

  // Escape, scroll lock, focus trap, and focus restore on close.
  useEffect(() => {
    if (!visible) return;
    const opener = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const els = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      const inside = dialogRef.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !inside)) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Move focus in unless the quiz already autofocused its first input.
    requestAnimationFrame(() => {
      if (dialogRef.current && !dialogRef.current.contains(document.activeElement)) {
        dialogRef.current.querySelector<HTMLElement>("input, " + FOCUSABLE)?.focus();
      }
    });
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [visible, onClose]);

  // One open and one close event per showing.
  useEffect(() => {
    if (!visible) return;
    onShown?.();
    siteEvents.act("quiz_open", { source });
    return () => siteEvents.act("quiz_close", { source });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, source]);

  if (!visible) return null;

  return (
    <div className="afq-backdrop" onClick={onClose}>
      <style>{MODAL_CSS}</style>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="afq-dialog-title" className="afq-dialog"
        onClick={(e) => e.stopPropagation()}>
        <div className="afq-head">
          <div>
            <div id="afq-dialog-title" style={{ fontSize: 15.5, fontWeight: 800, color: "#fff", letterSpacing: "-0.01em", fontFamily: '"Manrope", system-ui, sans-serif' }}>Find the jobs you are not seeing</div>
            <div style={{ fontSize: 12.5, color: "rgba(255,255,255,.68)", marginTop: 2 }}>Eleven questions, about two minutes. Then a plan and an email alert for your role.</div>
          </div>
          <button type="button" aria-label="Close" className="afq-close" onClick={onClose}
            style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "rgba(255,255,255,.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="afq-body">
          {data ? (
            <CopilotQuiz data={data} onDone={onClose} source={source} />
          ) : error ? (
            <div role="alert" style={{ fontFamily: '"Manrope", system-ui, sans-serif', paddingTop: 40, textAlign: "center" }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#0F2137" }}>The questions did not load</div>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: "#67788C", margin: "8px auto 18px", maxWidth: "36ch" }}>Your connection may have dropped. Nothing was lost.</p>
              <button type="button" onClick={retry}
                style={{ minHeight: 46, padding: "0 24px", background: "#0F2137", color: "#fff", border: "none", borderRadius: 11, fontFamily: "inherit", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>Try again</button>
            </div>
          ) : (
            <div role="status" style={{ fontFamily: '"Manrope", system-ui, sans-serif', paddingTop: 40, textAlign: "center", fontSize: 14, color: "#67788C" }}>
              Loading the questions...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
